import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import * as organizations from '../../../../../organizations'
import type * as orgAssignments from '../../../../record-assigned-orgs'
import type { Plugin } from '../plugin'

/**
 * Loads functional usage when an organization's joint use configuration is
 * updated and that organization is assigned to the current record as the
 * occupier.
 */
export const loadForCurrentEntityOnOrganizationChange = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    rxjs
      .combineLatest([
        ec.models.CollectionProducer.getEc$<orgAssignments.TypeParams>(
          ec.Name.Predefined.SpaceOrgAssignment,
          { waitUntilReady: true },
        ),
        ec.Collection.instances.find<organizations.Name>({
          isName: organizations.Name.isName,
        }).asObservable,
      ])
      .pipe(
        rxjs.switchMap(([orgAssignmentsEc, organizationEcs]) =>
          rxjs
            .merge(
              ...organizationEcs.map(organizationsEc =>
                organizationsEc.plugins.editingPlugin.feature.actions.submit.success.observe(),
              ),
            )
            .pipe(
              rxjs.withLatestFrom(
                plugin.selectors.functionalUsage.asObservable,
                orgAssignmentsEc.plugins.cachePlugin.feature.selectors.getCachedEntities()
                  .asObservable,
              ),
              rxjs.filter(
                ([
                  {
                    data: {
                      response: { updates: orgUpdates },
                    },
                  },
                  functionalUsage,
                  assignments,
                ]) => {
                  const updatedOrgIds = new Set(
                    orgUpdates.map(update => update.id),
                  )
                  const isAssignedOccupierOrgUpdated = assignments.some(
                    assignment =>
                      assignment?.isOccupier &&
                      Object.values(assignment.organizationLookup).some(orgId =>
                        updatedOrgIds.has(orgId),
                      ),
                  )

                  return (
                    isAssignedOccupierOrgUpdated &&
                    functionalUsage?.functionalizationMethodology ===
                      ents.extra.space.Record.FunctionalUsage.Methodology
                        .JointUse
                  )
                },
              ),
              rxjs.map(() =>
                feature.actions.loadForCurrentEntity.request
                  .from(
                    '(effect) load for current entity on organization change',
                  )
                  .create(),
              ),
            ),
        ),
      ),
)
